import React, { useState } from "react";
import { Plus } from "lucide-react";
import { BRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const [sizeOpen, setSizeOpen] = useState(false);
  const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
  const minPrice = hasSizes ? Math.min(...product.sizes.map((s) => s.price)) : product.price;

  const addSimple = () => {
    add(product);
    toast.success(`${product.name} adicionado ao pedido`);
  };

  const addWithSize = (size) => {
    add({
      id: `${product.id}:${size.name}`,
      name: `${product.name} (${size.name})`,
      price: size.price,
    });
    toast.success(`${product.name} (${size.name}) adicionado ao pedido`);
    setSizeOpen(false);
  };

  const onAdd = () => (hasSizes ? setSizeOpen(true) : addSimple());

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-card rounded-2xl border border-border overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-muted/60 to-muted/20 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-bold text-lg leading-tight tracking-tight">
          {product.name}
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed line-clamp-2 min-h-[2.5rem]">
          {product.description}
        </p>
        <div className="flex items-end justify-between gap-3 mt-5 pt-4 border-t border-border/70">
          <span className="font-display font-black text-xl text-primary">
            {hasSizes ? (
              <span className="flex flex-col leading-none">
                <span className="text-[10px] font-sans font-medium text-muted-foreground mb-0.5">a partir de</span>
                {BRL(minPrice)}
              </span>
            ) : (
              BRL(product.price)
            )}
          </span>
          <button
            onClick={onAdd}
            data-testid={`add-to-cart-${product.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" /> {hasSizes ? "Escolher" : "Adicionar"}
          </button>
        </div>
      </div>

      {hasSizes && (
        <Dialog open={sizeOpen} onOpenChange={setSizeOpen}>
          <DialogContent data-testid={`size-dialog-${product.id}`}>
            <DialogHeader>
              <DialogTitle className="font-display font-black text-xl tracking-tight">
                {product.name}
              </DialogTitle>
              <DialogDescription>Escolha o tamanho.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {product.sizes.map((s) => (
                <button
                  key={s.name}
                  onClick={() => addWithSize(s)}
                  data-testid={`size-option-${product.id}-${s.name}`}
                  className="w-full flex items-center justify-between rounded-xl border border-border px-4 py-3 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <span className="font-semibold">{s.name}</span>
                  <span className="font-display font-black text-primary">{BRL(s.price)}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
